/**
 * Prüfansicht eines ZEO-Schreibvorschlags (15.10.2026): Anliegen, Sachverhalt mit Belegart, Quellen und Prüfhinweise --
 * GETRENNT vom Brief. Nichts davon wird in den Brief oder ins PDF übernommen.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react';

const ART = {
  original: ['Original', 'bg-green-100 text-green-800'],
  mitteilung: ['Mitteilung Dritter', 'bg-amber-100 text-amber-800'],
  eigenes_schreiben: ['eigenes Schreiben', 'bg-slate-100 text-slate-700'],
  nutzerangabe: ['Nutzerangabe', 'bg-sky-100 text-sky-800'],
  unbestaetigt: ['nicht bestätigt', 'bg-red-100 text-red-800'],
};

function datumDe(d) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d || '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : (d || '');
}

export default function PruefAnsicht({ pruefung, offen: startOffen = false, testid = 'pruefansicht' }) {
  const [offen, setOffen] = useState(startOffen);
  if (!pruefung) return null;
  const { anliegen, sachverhalt = [], rueckfragen = [], quellen = [], hinweise = [], empfaenger_quelle: eq, kennungen = [], vorgang } = pruefung;
  return (
    <div className="rounded border border-slate-200 bg-slate-50 text-xs my-2" data-testid={testid}>
      <button type="button" onClick={() => setOffen((o) => !o)} className="w-full flex items-center gap-1.5 px-2 py-1.5 text-slate-700 font-medium"
        data-testid={`${testid}-umschalten`}>
        {offen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}<ShieldCheck size={13} /> Prüfansicht (nicht Teil des Briefes)
        {rueckfragen.length > 0 && <span className="ml-auto rounded bg-amber-200 text-amber-900 px-1.5">{rueckfragen.length} offen</span>}
      </button>
      {offen && (
        <div className="px-2 pb-2 space-y-2 text-slate-700">
          {anliegen && <div data-testid={`${testid}-anliegen`}><span className="text-slate-500">Anliegen:</span> {anliegen}</div>}
          {rueckfragen.length > 0 && (
            <div className="bg-amber-50 text-amber-900 rounded p-1.5" data-testid={`${testid}-rueckfragen`}>
              <div className="font-medium">Bitte klären:</div>
              {rueckfragen.map((r) => <div key={r}>• {r}</div>)}
            </div>)}
          {(eq || kennungen.length > 0 || vorgang) && (
            <div className="space-y-0.5" data-testid={`${testid}-felder`}>
              {eq && <div><span className="text-slate-500">Empfängeranschrift aus:</span> „{eq.titel}“{eq.datum ? ` (${datumDe(eq.datum)})` : ''}</div>}
              {kennungen.map((k) => <div key={k.art}><span className="text-slate-500">{k.art} aus:</span> „{k.titel}“{k.datum ? ` (${datumDe(k.datum)})` : ''}</div>)}
              {vorgang && <div><span className="text-slate-500">Vorgang:</span> {vorgang.bezeichnung}</div>}
            </div>)}
          {sachverhalt.length > 0 && (
            <div data-testid={`${testid}-sachverhalt`}>
              <div className="text-slate-500 mb-0.5">Sachverhalt und Belege:</div>
              {sachverhalt.map((s) => {
                const [label, cls] = ART[s.art] || [s.art, 'bg-slate-100'];
                return (
                  <div key={`${s.aussage}|${s.dokument_id || ''}`} className="flex gap-1.5 items-start py-0.5" data-testid={`${testid}-aussage`} data-art={s.art}>
                    <span className={`shrink-0 rounded px-1 ${cls}`}>{label}</span>
                    <span>{s.aussage}{s.titel ? <span className="text-slate-500"> – {s.beleg}, „{s.titel}“{s.datum ? ` ${datumDe(s.datum)}` : ''}</span> : <span className="text-slate-500"> – {s.beleg}</span>}</span>
                  </div>);
              })}
            </div>)}
          {hinweise.length > 0 && (
            <div className="space-y-0.5" data-testid={`${testid}-hinweise`}>{hinweise.map((h) => <div key={h}>⚠ {h}</div>)}</div>)}
          {quellen.length > 0 && (
            <div data-testid={`${testid}-quellen`}>
              <div className="text-slate-500 mb-0.5">Ausgewertete Originale ({quellen.length}):</div>
              {quellen.map((q) => <div key={q.id}>• {q.titel}{q.datum ? ` (${datumDe(q.datum)})` : ''} – {q.beleg}</div>)}
            </div>)}
        </div>)}
    </div>
  );
}
